import { Children, type CSSProperties, type ReactNode } from 'react'
import { UserRound } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { Avatar } from '../../ui/Avatar'

/**
 * A `row` (a window's few facts): one column per present fact, as wide as its content, spread evenly
 * so a longer fact (two instructors) takes the room it needs; stacked on phones.
 * A `column` (a page or summary whose facts may grow): one fact per line at every width.
 */
export function DetailsFacts({ children, layout = 'row', className }: { children: ReactNode; layout?: 'row' | 'column'; className?: string }) {
    const count = Children.toArray(children).length
    if (!count) return null
    return (
        <div className={cn(layout === 'row'
            ? 'grid gap-3 sm:grid-cols-[repeat(var(--details-fact-count),minmax(0,max-content))] sm:justify-evenly sm:gap-6'
            : 'flex flex-col gap-3', className)}
            style={layout === 'row' ? { '--details-fact-count': count } as CSSProperties : undefined}>
            {children}
        </div>
    )
}

export function DetailsFact({ icon, label, children, className }: { icon: ReactNode; label: string; children: ReactNode; className?: string }) {
    return (
        <div className={cn('flex min-w-0 items-start gap-2.5', className)}>
            {icon}
            <div className="min-w-0">
                <p className="text-xs leading-4 text-gray-500 dark:text-gray-400">{label}</p>
                <div className="mt-0.5 break-words text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">{children}</div>
            </div>
        </div>
    )
}

/** Brand-tinted (the studio accent on public pages, the app primary elsewhere), or gray when the class cannot be booked, matching the disabled footer. */
export function DetailsFactIcon({ children, muted }: { children: ReactNode; muted: boolean }) {
    return (
        <span aria-hidden="true" className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            muted ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500' : 'bg-[var(--landing-primary-50,var(--color-primary-50))] text-[var(--landing-primary-500,var(--color-primary-500))] dark:bg-primary-900/30 dark:text-primary-300',
        )}>
            {children}
        </span>
    )
}

/**
 * The people of an instructors fact: photos, or the person icon when none has one. A column of facts,
 * and a row stacked on a phone, show one photo, so every label starts at the same edge; a row has room for two.
 */
export function DetailsPeopleIcon({ people, muted, max = 2 }: { people: { key: string; name: string; avatarUrl?: string | null }[]; muted: boolean; max?: 1 | 2 }) {
    const photos = people.filter(person => person.avatarUrl).slice(0, max)
    if (!photos.length) return <DetailsFactIcon muted={muted}><UserRound className="h-4 w-4"/></DetailsFactIcon>
    return (
        <span className="flex -space-x-2" aria-hidden="true">
            {photos.map((person, index) => <Avatar key={person.key} name={person.name} src={person.avatarUrl ?? undefined} size="small" className={cn('h-8 w-8 ring-2 ring-white dark:ring-gray-900', index > 0 && 'max-sm:hidden')}/>)}
        </span>
    )
}
